import React from 'react';
import { Shield, Lock, FileText } from 'lucide-react';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import SEOHead from '../components/SEOHead';

export default function PrivacyPolicy() {
  return (
    <div className="bg-[#070E1B] text-white min-h-screen font-opensans flex flex-col justify-between">
      <SEOHead 
        title="Privacy Policy | Grow & Stay Rich"
        description="Learn how Grow & Stay Rich protects your privacy, handles financial consultation data, complies with GDPR, CCPA, and TCPA text messaging regulations."
        path="/privacy"
      />

      <div>
        <Navigation />

        <div className="relative pt-24 pb-16 border-b border-[#C9A84C]/25 overflow-hidden">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 space-y-4">
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-[#162236] border border-[#C9A84C]/30 text-xs font-semibold text-[#E8D4A0] tracking-wider uppercase">
              <Shield className="w-4 h-4 text-[#C9A84C]" />
              Data Protection & Privacy Standards
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold font-playfair text-white">Privacy Policy</h1>
            <p className="text-sm text-slate-300">Effective Date: January 1, 2026 • Last Updated: August 2026</p>
          </div>
        </div>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 space-y-8 text-sm text-slate-300 leading-relaxed font-opensans">
          <section className="bg-[#0D1B2A]/90 border border-[#C9A84C]/25 p-8 rounded-2xl space-y-4">
            <h2 className="text-xl font-bold font-playfair text-[#E8D4A0]">1. Introduction & Scope</h2>
            <p>
              Grow & Stay Rich ("we," "our," or "us") is committed to maintaining the confidentiality, integrity, and security of personal information entrusted to us by clients, financial consultation registrants, and website visitors. This Privacy Policy outlines how we collect, store, process, and protect your data in compliance with state insurance regulations, the California Consumer Privacy Act (CCPA), the General Data Protection Regulation (GDPR), and the Telephone Consumer Protection Act (TCPA).
            </p>
          </section>

          <section className="bg-[#0D1B2A]/90 border border-[#C9A84C]/25 p-8 rounded-2xl space-y-4">
            <h2 className="text-xl font-bold font-playfair text-[#E8D4A0]">2. Information We Collect</h2>
            <p>We collect personal information necessary to deliver financial educational materials, schedule advisory consultations, and generate custom Financial Needs Analyses (FNA). This includes:</p>
            <ul className="list-disc pl-5 space-y-1 text-slate-300">
              <li><strong>Contact Information:</strong> Full name, email address, telephone number, and state of residence.</li>
              <li><strong>Consultation Data:</strong> Key financial priorities, retirement timelines, and strategic objectives voluntarily submitted in booking forms.</li>
              <li><strong>Technical Metadata:</strong> IP address, browser type, device information, and anonymous analytical telemetry.</li>
            </ul>
          </section>

          <section className="bg-[#0D1B2A]/90 border border-[#C9A84C]/25 p-8 rounded-2xl space-y-4">
            <h2 className="text-xl font-bold font-playfair text-[#E8D4A0]">3. TCPA Text Messaging & Communications Consent</h2>
            <p>
              By providing your telephone number on our booking, lead magnet, webinar, or contact forms, you grant explicit consent for Grow & Stay Rich to contact you via telephone call or SMS text message for consultation scheduling, appointment reminders, and requested financial updates. Message and data rates may apply. You may opt out of receiving SMS communications at any time by replying "STOP" or contacting us at <strong>info@growandstayrich.com</strong> or <strong>(903) 231-3051</strong>.
            </p>
          </section>

          <section className="bg-[#0D1B2A]/90 border border-[#C9A84C]/25 p-8 rounded-2xl space-y-4">
            <h2 className="text-xl font-bold font-playfair text-[#E8D4A0]">4. Data Confidentiality & Non-Sale Pledge</h2>
            <p>
              We <strong>never sell, rent, or trade</strong> your personal information to third-party marketing brokers. Personal data is shared strictly with authorized carriers (such as Allianz, Corebridge, Nationwide, Athene, etc.) only when necessary to process requested insurance illustrations, underwriting applications, or carrier policy quotes.
            </p>
          </section>

          <section className="bg-[#0D1B2A]/90 border border-[#C9A84C]/25 p-8 rounded-2xl space-y-4">
            <h2 className="text-xl font-bold font-playfair text-[#E8D4A0]">5. Contact Us Regarding Your Rights</h2>
            <p>
              If you wish to review, update, or delete your personal information stored with us, please submit a written request to:
            </p>
            <div className="font-mono text-xs bg-[#162236] p-4 rounded-xl border border-[#C9A84C]/20 text-slate-300">
              Grow & Stay Rich Compliance Department<br />
              2019 Holy Unity Ln, St Paul, TX-75098<br />
              Email: info@growandstayrich.com • Direct Line: (903) 231-3051
            </div>
          </section>
        </div>
      </div>

      <Footer />
    </div>
  );
}
